import CurrencyInput from './CurrencyInput.jsx';
import { maxLengthFor, isOverLength, isNonInteger } from '../schema/fieldLimits.js';
import { isDateOutOfRange, getDateRange, getDateRangeHint, isNumberOutOfRange, isInvalidEmail, isInvalidPhone, formatPhone } from '../utils/validation.js';

// Trim leading/trailing whitespace on blur so a whitespace-only entry collapses
// to empty. Without this, "   " in a required field looked filled and slipped
// past the auto-save (which only blocks a truly empty name).
function trimOnBlur(value, onChange) {
  return e => {
    const trimmed = (e.target.value ?? '').trim();
    if (trimmed !== (value ?? '')) onChange(trimmed);
  };
}

// Map the legacy `width` setting (in pqSections schema) to a grid span class.
const SPAN_BY_WIDTH = {
  wide: 'f-6',
  medium: 'f-3',
  field: 'f-3',
  'field-2': 'f-2',
  'field-4': 'f-4',
  'field-5': 'f-5',
};

export default function Field({ field, value, onChange }) {
  let cls = 'field ' + (SPAN_BY_WIDTH[field.width] || 'f-3');
  if (field.type === 'textarea') cls += ' f-12';
  if (field.type === 'computed' || field.locked) cls += ' readonly';
  if (field.emphasis === 'total') cls += ' field-total';
  // Force this field to start a fresh grid row (used to drop Date of Birth down
  // next to Age instead of trailing the name fields).
  if (field.newRow) cls += ' field-newrow';

  // Use <div> for fields that contain multiple form controls (multiselect,
  // future radio groups). A <label> wrapper would forward stray clicks to
  // the first control inside.
  const Wrapper = field.type === 'multiselect' ? 'div' : 'label';

  return (
    // data-field lets the save banner scroll to a specific field when Salesforce
    // rejects its value. Table cells already carry data-col; scalar fields had
    // nothing to aim at, so "Jump to the problem" silently did nothing for them.
    <Wrapper className={cls} data-field={field.key}>
      <div className="label-row">
        <span className="label">{field.label}</span>
        {field.required && (
          <span className="label-tag tag-required">REQ</span>
        )}
        {field.emoney && (
          <span
            className="emoney-dot"
            role="img"
            tabIndex={0}
            aria-label="Needed for eMoney, will be required on Plan Order submission."
            title="Needed for eMoney, will be required on Plan Order submission."
          />
        )}
        {field.locked && field.lockHint && (
          <span className="label-tag tag-locked" title={field.lockHint}>
            LOCKED
          </span>
        )}
      </div>
      {renderControl(field, value, onChange)}
    </Wrapper>
  );
}

function renderControl(field, value, onChange) {
  const v = value ?? '';

  // Locked fields belong to a record this launch may not write to (today: the
  // household's own people and contact details, see householdOwned.js). They
  // render as plain read-only text whatever their type, because the point is
  // that the value is shown and cannot be changed here — a disabled date or
  // number input would still invite the attempt.
  if (field.locked) {
    return (
      <>
        <input type="text" value={v} className="readonly" readOnly />
        {field.lockHint && <span className="field-hint">{field.lockHint}</span>}
      </>
    );
  }

  switch (field.type) {
    case 'textarea':
      return (
        <textarea
          value={v}
          onChange={e => onChange(e.target.value)}
          onBlur={trimOnBlur(v, onChange)}
          required={field.required || undefined}
        />
      );

    case 'select':
      return (
        <select
          value={v}
          onChange={e => onChange(e.target.value)}
          required={field.required || undefined}
        >
          <option value=""></option>
          {(field.options || []).map(opt => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );

    case 'multiselect': {
      const selected = Array.isArray(value) ? value : [];
      // An option named in `exclusiveOption` cannot be combined with any other:
      // picking it clears the rest, and picking anything else clears it. That is
      // what lets "None" mean "we asked and they have none" rather than being
      // indistinguishable from an untouched field, without ever recording the
      // contradiction "None + Trust". Same shape as the Assets section, where
      // ticking "Own your home?" clears "Renting?".
      const exclusive = field.exclusiveOption;
      const toggle = opt => {
        const has = selected.includes(opt);
        let next;
        if (has) {
          next = selected.filter(x => x !== opt);
        } else if (exclusive && opt === exclusive) {
          next = [opt];
        } else {
          next = [...selected.filter(x => x !== exclusive), opt];
        }
        onChange(next);
      };
      return (
        <div className="multiselect-group">
          {(field.options || []).map(opt => (
            <label key={opt} className="multiselect-item">
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={() => toggle(opt)}
              />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      );
    }

    case 'currency':
      return <CurrencyInput value={v} onChange={onChange} />;

    case 'computed':
      return <input type="text" value={v} className="num readonly" readOnly />;

    case 'number': {
      // See TableCell: a decimal on a whole-number Salesforce field fails in
      // the Apex deserializer and takes the whole record with it.
      const fraction = field.integer && isNonInteger(v);
      const outOfRange = isNumberOutOfRange(v, field.min, field.max);
      const invalid = outOfRange || fraction;
      return (
        <>
          <input
            type="number"
            className={'num' + (invalid ? ' date-invalid' : '')}
            step={field.integer ? 1 : undefined}
            min={field.min}
            max={field.max}
            title={outOfRange ? `Enter a value between ${field.min ?? '–∞'} and ${field.max ?? '∞'}.` : undefined}
            value={v}
            onChange={e => onChange(e.target.value)}
            aria-invalid={invalid || undefined}
            required={field.required || undefined}
          />
          {fraction && (
            <span className="field-hint-error">
              Whole numbers only, or this will not save.
            </span>
          )}
        </>
      );
    }

    case 'date': {
      const { min, max } = getDateRange(field.dateRange);
      const invalid = isDateOutOfRange(v, min, max);
      return (
        <>
          <input
            type="date"
            className={invalid ? 'date-invalid' : undefined}
            min={min}
            max={max}
            value={v}
            onChange={e => onChange(e.target.value)}
            required={field.required || undefined}
            aria-invalid={invalid || undefined}
          />
          {invalid && <span className="field-hint-error">{getDateRangeHint(field.dateRange)}</span>}
        </>
      );
    }

    case 'email': {
      const invalid = isInvalidEmail(v);
      return (
        <>
          <input
            type="email"
            autoComplete="off"
            className={invalid ? 'date-invalid' : undefined}
            value={v}
            onChange={e => onChange(e.target.value)}
            onBlur={trimOnBlur(v, onChange)}
            required={field.required || undefined}
            aria-invalid={invalid || undefined}
          />
          {invalid && (
            <span className="field-hint-error">
              Enter a valid email address (like name@example.com).
            </span>
          )}
        </>
      );
    }

    case 'tel': {
      const invalid = isInvalidPhone(v);
      return (
        <>
          <input
            type="tel"
            autoComplete="off"
            className={invalid ? 'date-invalid' : undefined}
            value={v}
            onChange={e => onChange(e.target.value)}
            // Format to (123) 456-7890 on blur; falls back to a plain trim for
            // anything that isn't a recognizable 10/11-digit number.
            onBlur={e => {
              const formatted = formatPhone(e.target.value);
              if (formatted !== (v ?? '')) onChange(formatted);
            }}
            required={field.required || undefined}
            aria-invalid={invalid || undefined}
          />
          {invalid && (
            <span className="field-hint-error">Enter a 10-digit phone number.</span>
          )}
        </>
      );
    }

    default: {
      // maxLength mirrors the Salesforce column so an over-length value cannot
      // be typed at all. tooLong covers what typing cannot: a value already in
      // the record from before this guard, which still has to be flagged
      // because it is what keeps the whole record from saving.
      const max = maxLengthFor(field);
      const tooLong = isOverLength(v, max);
      // autoComplete off: these fields hold CLIENT data — the browser
      // suggesting the advisor's own saved addresses/emails is noise at best
      // and a mis-fill risk at worst (UAT: Eugene, 7/8).
      return (
        <>
          <input
            type="text"
            autoComplete="off"
            className={tooLong ? 'date-invalid' : undefined}
            maxLength={max}
            value={v}
            onChange={e => onChange(e.target.value)}
            onBlur={trimOnBlur(v, onChange)}
            required={field.required || undefined}
            aria-invalid={tooLong || undefined}
          />
          {tooLong && (
            <span className="field-hint-error">
              Must be {max} characters or fewer, or this will not save.
            </span>
          )}
        </>
      );
    }
  }
}
